import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export type Coordinates = [number, number];
export type Area = {
  _id: string; name: string; name_en: string; aliases: string[]; province: string; province_th: string;
  center: { type: 'Point'; coordinates: Coordinates }; radius_m: number;
  boundary: { type: 'Polygon'; coordinates: Coordinates[][] } | null;
  status: 'active' | 'inactive'; revision: number; boundary_kind?: string;
  shop_count?: number; visible_shop_count?: number; overlap_shop_count?: number;
  provenance?: { anchor_name?: string; google_maps_uri?: string; boundary_method?: string };
};
export type Shop = { _id: string; name: string; coordinates: Coordinates; neighborhood_ids: string[]; visible: boolean; included?: boolean; automatic?: boolean; override?: "auto" | "include" | "exclude"; revision?: number };
export type MapMode = 'view' | 'center' | 'polygon';
const latLng = ([lng, lat]: Coordinates): L.LatLngTuple => [lat, lng];

export function NeighborhoodMap({ area, neighbors, shops, mode, vertices, onPoint }: {
  area: Area; neighbors: Area[]; shops: Shop[]; mode: MapMode; vertices: Coordinates[];
  onPoint: (point: Coordinates) => void;
}) {
  const element = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const pointHandler = useRef(onPoint);
  pointHandler.current = onPoint;
  useEffect(() => {
    if (!element.current) return;
    const instance = L.map(element.current, { scrollWheelZoom: false }).setView(latLng(area.center.coordinates), 14);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(instance);
    layer.current = L.layerGroup().addTo(instance);
    instance.on('click', (event: L.LeafletMouseEvent) => pointHandler.current([event.latlng.lng, event.latlng.lat]));
    const resize = new ResizeObserver(() => instance.invalidateSize());
    resize.observe(element.current);
    map.current = instance;
    return () => { resize.disconnect(); instance.remove(); map.current = null; layer.current = null; };
    // The map stays mounted while edits redraw its vector layer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const coordinates = area.boundary?.coordinates[0];
    const bounds = coordinates
      ? L.latLngBounds(coordinates.map(latLng))
      : L.latLng(latLng(area.center.coordinates)).toBounds(area.radius_m * 2);
    if (bounds.isValid()) instance.fitBounds(bounds.pad(0.3), { animate: false, maxZoom: 16 });
    // Reframe when selecting a different area, while preserving pan during drawing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area._id, area.province]);
  useEffect(() => {
    const group = layer.current;
    if (!group) return;
    group.clearLayers();
    const shape = (item: Area, selected: boolean) => {
      const options = { color: selected ? '#255cf4' : '#9770d2', weight: selected ? 3 : 1.5,
        fillOpacity: selected ? 0.12 : 0.035, dashArray: selected ? undefined : '5 5', interactive: false };
      const path = item.boundary
        ? L.polygon(item.boundary.coordinates[0].map(latLng), options)
        : L.circle(latLng(item.center.coordinates), { ...options, radius: item.radius_m });
      path.addTo(group);
    };
    neighbors.filter(item => item.status === 'active' && item._id !== area._id).forEach(item => shape(item, false));
    if (area.radius_m >= 100 && Number.isFinite(area.radius_m) && area.center.coordinates.every(Number.isFinite)) {
      shape(area, true);
      L.circleMarker(latLng(area.center.coordinates), { radius: 6, weight: 3, color: '#fff', fillColor: '#255cf4', fillOpacity: 1, interactive: false }).addTo(group);
    }
    for (const shop of shops) {
      const overlap = shop.neighborhood_ids.length > 1;
      const marker = L.circleMarker(latLng(shop.coordinates), { radius: 4, weight: 1, color: '#fff',
        fillColor: overlap ? '#ed9831' : '#1d9a75', fillOpacity: shop.visible ? 0.9 : 0.35 });
      const tooltip = document.createElement('span');
      tooltip.textContent = shop.name + (overlap ? ` · อยู่ใน ${shop.neighborhood_ids.length} ย่าน` : '');
      marker.bindTooltip(tooltip).addTo(group);
    }
    if (mode === 'polygon' && vertices.length) {
      L.polyline(vertices.map(latLng), { color: '#255cf4', weight: 3, dashArray: '6 5', interactive: false }).addTo(group);
      vertices.forEach(point => L.circleMarker(latLng(point), { radius: 5, color: '#fff', fillColor: '#255cf4', fillOpacity: 1, interactive: false }).addTo(group));
    }
  }, [area, neighbors, shops, mode, vertices]);
  // Keep className stable: Leaflet adds its own classes to this DOM element.
  return <div ref={element} className="neighborhoods__map" data-mode={mode} aria-label="แผนที่ขอบเขตย่าน" />;
}
